import React, { useMemo } from 'react';
import './LetterGrid.css';

// Columns per letter count; 9 keeps the classic 3×3 board.
const COLUMNS_BY_COUNT = { 4: 4, 5: 5, 6: 3, 7: 4, 8: 4, 9: 3 };

export function gridColumnsFor(count) {
  return COLUMNS_BY_COUNT[count] || Math.min(Math.max(count, 1), 5);
}

function LetterGrid({
  letters = '',
  count = 9,
  size = 'md',
  label = 'Перемешанные буквы',
  className = '',
  usedIndices = null,
  onLetterClick = null,
  hint = false,
  // Size tiles as if the board had this many columns, so they keep one size across word lengths.
  tileColumns = null
}) {
  const cells = useMemo(() => {
    const raw = typeof letters === 'string' ? letters.split('') : Array.from(letters || []);
    return Array.from({ length: count }, (_, i) => raw[i] || '');
  }, [letters, count]);

  const interactive = typeof onLetterClick === 'function';
  const columns = gridColumnsFor(count);
  // Each tile spans two half-columns so a short last row can be centered.
  const lastRowCount = count % columns;
  const lastRowStart = lastRowCount ? count - lastRowCount : -1;
  const lastRowOffset = columns - lastRowCount;
  const fixedTiles = Boolean(tileColumns);
  const gridStyle = fixedTiles
    ? {
        '--tile-columns': tileColumns,
        gridTemplateColumns: `repeat(${columns * 2}, calc((var(--tile) - var(--grid-gap)) / 2))`
      }
    : { gridTemplateColumns: `repeat(${columns * 2}, 1fr)` };

  return (
    <div
      className={['letter-grid-wrap', fixedTiles ? 'letter-grid-wrap--fixed-tiles' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          'letter-grid',
          `letter-grid--${size}`,
          `letter-grid--cols-${columns}`,
          fixedTiles ? 'letter-grid--fixed-tiles' : '',
          interactive ? 'letter-grid--interactive' : '',
          interactive && hint ? 'letter-grid--hint' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        style={gridStyle}
        role={interactive ? 'group' : 'img'}
        aria-label={label}
      >
        {cells.map((letter, i) => {
          const used = usedIndices?.has?.(i);
          const classNameCell = [
            'letter-grid__cell',
            used ? 'letter-grid__cell--used' : '',
            interactive && letter ? 'letter-grid__cell--button' : ''
          ]
            .filter(Boolean)
            .join(' ');
          const style = {
            gridColumn: i === lastRowStart ? `${lastRowOffset + 1} / span 2` : 'span 2'
          };

          if (interactive && letter) {
            return (
              <button
                key={i}
                type="button"
                className={classNameCell}
                style={style}
                aria-pressed={used ? 'true' : 'false'}
                aria-label={used ? `Убрать букву ${letter}` : `Добавить букву ${letter}`}
                onClick={() => onLetterClick(i, letter)}
              >
                {letter}
              </button>
            );
          }

          return (
            <div key={i} className={classNameCell} style={style}>
              {letter}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LetterGrid;
